import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "@repo/api-client";
import type { AppTheme, SiteNavLink } from "@repo/shared-types";

export type { AppTheme };

export interface SiteSettings {
  theme: AppTheme;
  siteName: string;
  tagline: string;
  primaryColor: string;
  enableGamification: boolean;
  enableNewsletter: boolean;
  maintenanceMode: boolean;
  headerLinks: SiteNavLink[];
  footerLinks: SiteNavLink[];
}

interface ThemeContextValue {
  settings: SiteSettings;
  updateSettings: (partial: Partial<SiteSettings>) => void;
  setTheme: (theme: AppTheme) => void;
}

const STORAGE_KEY = "wtm_site_settings";

const defaultSettings: SiteSettings = {
  theme: "modern",
  siteName: "WayToMoksha",
  tagline: "Ancient wisdom for modern seekers",
  primaryColor: "#D4A853",
  enableGamification: true,
  enableNewsletter: true,
  maintenanceMode: false,
  headerLinks: [
    { id: "h-home", label: "Home", href: "/", kind: "internal", area: "header", enabled: true, order: 1 },
    { id: "h-wisdom", label: "Wisdom", href: "/wisdom", kind: "internal", area: "header", enabled: true, order: 2 },
    { id: "h-courses", label: "Courses", href: "/courses", kind: "internal", area: "header", enabled: true, order: 3 },
    { id: "h-events", label: "Events", href: "/events", kind: "internal", area: "header", enabled: true, order: 4 },
    { id: "h-wellness", label: "Wellness", href: "/wellness", kind: "internal", area: "header", enabled: true, order: 5 },
    { id: "h-stories", label: "Stories", href: "/stories", kind: "internal", area: "header", enabled: true, order: 6 },
    { id: "h-library", label: "Library", href: "/library", kind: "internal", area: "header", enabled: true, order: 7 },
    { id: "h-teachers", label: "Teachers", href: "/teachers", kind: "internal", area: "header", enabled: true, order: 8 },
    { id: "h-practice", label: "Practice", href: "/practice", kind: "internal", area: "header", enabled: true, order: 9 },
    { id: "h-shop", label: "Shop", href: "/shop", kind: "internal", area: "header", enabled: true, order: 10 },
  ],
  footerLinks: [
    { id: "f-about", label: "About", href: "/about", kind: "internal", area: "footer", enabled: true, order: 1 },
    { id: "f-contact", label: "Contact", href: "/contact", kind: "internal", area: "footer", enabled: true, order: 2 },
    { id: "f-donate", label: "Donate", href: "/donate", kind: "internal", area: "footer", enabled: true, order: 3 },
    { id: "f-events", label: "Events", href: "/events", kind: "internal", area: "footer", enabled: true, order: 4 },
  ],
};

const ThemeContext = createContext<ThemeContextValue>({
  settings: defaultSettings,
  updateSettings: () => {},
  setTheme: () => {},
});

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [settings, setSettings] = useState<SiteSettings>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) return { ...defaultSettings, ...JSON.parse(stored) };
    } catch {
      /* ignore parse errors */
    }
    return defaultSettings;
  });

  // Persist to localStorage whenever settings change
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  }, [settings]);

  // Apply theme class and settings to <html> so variables cascade app-wide.
  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove("theme-modern", "theme-zen");
    root.classList.add(`theme-${settings.theme}`);
    root.style.setProperty("--c-brand", settings.primaryColor);
    root.dataset.newsletter = String(settings.enableNewsletter);
    root.dataset.gamification = String(settings.enableGamification);
    root.dataset.maintenance = String(settings.maintenanceMode);
  }, [settings.theme]);

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty("--c-brand", settings.primaryColor);
    root.dataset.newsletter = String(settings.enableNewsletter);
    root.dataset.gamification = String(settings.enableGamification);
    root.dataset.maintenance = String(settings.maintenanceMode);
  }, [settings.primaryColor, settings.enableGamification, settings.enableNewsletter, settings.maintenanceMode]);

  useEffect(() => {
    let active = true;

    api
      .getPublicSiteSettings()
      .then((remote) => {
        if (!active) return;
        setSettings((prev) => ({
          ...prev,
          ...remote,
        }));
      })
      .catch(() => {
        // Keep local defaults if remote settings are unavailable.
      });

    return () => {
      active = false;
    };
  }, []);

  const updateSettings = useCallback((partial: Partial<SiteSettings>) => {
    setSettings((prev) => ({ ...prev, ...partial }));
  }, []);

  const setTheme = useCallback((theme: AppTheme) => {
    updateSettings({ theme });
  }, [updateSettings]);

  return (
    <ThemeContext.Provider value={{ settings, updateSettings, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
